import React, { useState, useEffect } from 'react';
import Modal from '../Modal/Modal';
import './FilterBar.css';
import CustomSelect from '../CustomSelect';
import { useComponentLoading } from '../../utils/useComponentLoading';

const FilterBar = ({ filterOptions, onFilterChange, apiFunctions }) => {
    const [selectedFilter, setSelectedFilter] = useState(filterOptions[0].value);
    const [filterValues, setFilterValues] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [isModalOpen, setModalOpen] = useState(false);
    const [data, setData] = useState({});
    
    // Replace local loading state with useComponentLoading hook
    const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

    // useEffect(() => {
    //     console.log("FilterBar Re-rendered");
    //   });

    const fetchFilterData = async (filterType) => {
        // Don't fetch if we already have the data or if it's not a filter that needs data
        if (!filterType || !apiFunctions[filterType] || 
            (Array.isArray(data[filterType]) && data[filterType].length > 0) ||
            filterType === 'status' || filterType === 'time' || filterType === 'date') {
            return;
        }

        // Use withLoading instead of local loading state
        await withLoading(
            `fetch${filterType.charAt(0).toUpperCase() + filterType.slice(1)}`,
            async (onProgress) => {
                try {
                    onProgress(10); // Start progress
                    const params = new URLSearchParams({
                        page_size: 1000,
                        page: 1,
                    });
                    onProgress(30); // Progress before API call
                    const response = await apiFunctions[filterType](params);
                    onProgress(60); // Progress after API call
                    if (!response.ok) throw new Error('Network response was not ok');
                    const result = await response.json();
                    onProgress(90); // Progress after parsing
                    setData(prevData => ({ ...prevData, [filterType]: result }));
                    onProgress(100); // Complete
                    return result;
                } catch (error) {
                    console.error(`Error fetching ${filterType}:`, error);
                    throw error;
                }
            },
            `Loading ${filterType} data...`,
            30000
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterValues({ ...filterValues, [name]: value });
    };

    const handleFilterTypeChange = (option) => {
        if (!option) {
            setSelectedFilter(null);
            return;
        }
        const newFilterType = option.value;
        setSelectedFilter(newFilterType);
        
        // Only fetch data for filters that need it
        if (['company', 'driver', 'car'].includes(newFilterType)) {
            fetchFilterData(newFilterType);
        }
    };

    const handleSelectionChange = (selectedOption) => {
        if (selectedFilter === 'status') {
            setFilterValues(prev => ({ ...prev, status: selectedOption ? selectedOption.value : '' }));
        } else {
            setFilterValues(prev => ({ ...prev, [`${selectedFilter}Id`]: selectedOption ? selectedOption.value : '' }));
        }
    };

    const getOptionsForFilter = () => {
        if (selectedFilter === 'status') {
            return [
                { value: 'created', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
            ];
        }
        if (selectedFilter === 'car') {
            return data[selectedFilter]?.map(item => ({
                value: item.id,
                label: `${item.vehicleName} (${item.vehicleNo})`
            })) || [];
        }
        return data[selectedFilter]?.map(item => ({
            value: item.id,
            label: item.name || item.firstName || item.vehicleName
        })) || [];
    };

    const handleApplyFilter = () => {
        const updatedFilters = { ...filterValues };
    
        if (selectedFilter === 'company') {
            updatedFilters.companyName = data.company?.find(c => c.id === updatedFilters.companyId)?.name || '';
        }
        if (selectedFilter === 'driver') {
            updatedFilters.driverName = data.driver?.find(d => d.id === updatedFilters.driverId)?.firstName || '';
        }
        if (selectedFilter === 'car') {
            updatedFilters.carName = data.car?.find(car => car.id === updatedFilters.carId)?.vehicleName || '';
        }
    
        onFilterChange(updatedFilters);
        setAppliedFilters(updatedFilters);
    
        if (selectedFilter === 'time') setModalOpen(false);
        if (selectedFilter === 'date') setModalOpen(false);
    };

    const handleClearFilter = () => {
        setFilterValues({});
        setAppliedFilters({});
        onFilterChange({});
    };

    const toggleModal = () => {
        setModalOpen(!isModalOpen);
    };

    // Determine if any filter data is currently loading
    const isFilterLoading = selectedFilter ? isLoading(`fetch${selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}`) : false;
    const loadingMessage = selectedFilter ? getMessage(`fetch${selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}`) : '';
    const loadingProgress = selectedFilter ? getProgress(`fetch${selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}`) : 0;

    return (
        <div className="filter-bar">
            <div className='filter-bar-only'>
                <span>Filter By:</span>
                <CustomSelect
                    className="filter-dropdown"
                    options={filterOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                    onChange={handleFilterTypeChange}
                    value={selectedFilter}
                    placeholder="Search and select filter"
                    isSearchable
                    isSmall={true}
                />
                {['company', 'driver', 'car', 'status'].includes(selectedFilter) && (
                    <CustomSelect
                        className="filter-input"
                        options={getOptionsForFilter()}
                        onChange={handleSelectionChange}
                        value={selectedFilter === 'status' ? filterValues.status : filterValues[`${selectedFilter}Id`]}
                        placeholder={`Select ${selectedFilter}`}
                        isSearchable
                        isLoading={isFilterLoading}
                        isSmall={true}
                    />
                )}
                {selectedFilter === 'time' && (
                    <div>
                        <button className="custom-button" onClick={toggleModal}>
                            Select Date Range
                        </button>
                        <Modal isOpen={isModalOpen} onClose={toggleModal} title="Date Range">
                            <form>
                                <label> Start Time: </label>
                                <input
                                    type="datetime-local"
                                    name="startTime"
                                    className="filter-input"
                                    value={filterValues.startTime || ''}
                                    onChange={handleFilterChange}
                                />
                                <label>End Time:</label>
                                <input
                                    type="datetime-local"
                                    name="endTime"
                                    className="filter-input"
                                    value={filterValues.endTime || ''}
                                    onChange={handleFilterChange}
                                />
                                <div>
                                    <button className="custom-button" onClick={handleApplyFilter} style={{ width: '100%' }}>
                                        Apply
                                    </button>
                                </div>
                            </form>
                        </Modal>
                    </div>
                )}{selectedFilter === 'date' && (
                    <div>
                        <button className="custom-button" onClick={toggleModal}>
                            Select Date
                        </button>
                        <Modal isOpen={isModalOpen} onClose={toggleModal}>
                            <form>
                                <label> Start Date: </label>
                                <input
                                    type="date"
                                    name="startTime"
                                    className="filter-input"
                                    value={filterValues.startTime || ''}
                                    onChange={handleFilterChange}
                                />
                                <label>End Date:</label>
                                <input
                                    type="date"
                                    name="endTime"
                                    className="filter-input"
                                    value={filterValues.endTime || ''}
                                    onChange={handleFilterChange}
                                />
                                <div>
                                    <button className="custom-button" type="button" onClick={handleApplyFilter}>
                                        Apply
                                    </button>
                                </div>
                            </form>
                        </Modal>
                    </div>
                )}
                {Object.keys(filterValues).length > 0 && (
                    <button className="custom-button" onClick={handleApplyFilter}>
                        Apply Filter
                    </button>
                )}
                <button className="custom-button" onClick={handleClearFilter}>
                    Clear Filter
                </button>
            </div>
            <div className="applied-filters">
                {appliedFilters.companyName && (
                    <span className="filter-tag">
                        Company: { appliedFilters.companyName}
                    </span>
                )}
                {appliedFilters.driverName && (
                    <span className="filter-tag">
                        Driver: { appliedFilters.driverName}
                    </span>
                )}
                {appliedFilters.carName && (
                    <span className="filter-tag">
                        Car: { appliedFilters.carName}
                    </span>
                )}
                {Object.keys(appliedFilters)
                    .filter(key => key !== 'companyId' && key !== 'companyName' && key !== 'driverId' && key !== 'driverName' && key !== 'carId' && key !== 'carName')
                    .map((key) => (
                        <span key={key} className="filter-tag">
                            {`${key}: ${ appliedFilters[key]}`}
                        </span>
                    ))}
            </div>
        </div>
    );
};

export default FilterBar;
